import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X, ArrowLeft, Zap, Instagram, MessageCircle, Mail, Star, Gamepad2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../config/api';

const PricingPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [prices, setPrices] = useState({
    pro_monthly: 37500,
    pro_annual: 125000,
    simracing_monthly: 30000
  });

  // Check if user is logged in and load prices
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [authRes, pricesRes] = await Promise.all([
          axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => null),
          axios.get(`${API}/payments/prices`).catch(() => ({ data: { prices: {} } }))
        ]);
        
        if (authRes?.data) {
          setUser(authRes.data);
        }
        
        if (pricesRes?.data?.prices) {
          setPrices(pricesRes.data.prices);
        }
      } catch (error) {
        // Ignore errors
      }
    };
    loadInitialData();
  }, []);

  const formatPriceARS = (price) => {
    return new Intl.NumberFormat('es-AR', { 
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleUpgrade = async (planId) => {
    if (!user) {
      toast.info('Inicia sesión para suscribirte');
      navigate('/login');
      return;
    }

    // Check if user already has the plan or a better one
    if (planId.startsWith('pro_') && (user.plan === 'pro' || user.plan === 'simracing')) {
      toast.info('Ya tienes acceso a las funciones PRO');
      return;
    }
    
    if (planId === 'simracing_monthly' && user.plan === 'simracing') {
      toast.info('Ya tienes el plan Simracing');
      return;
    }

    setLoading(true);
    setSelectedPlan(planId);
    
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin
      }, { withCredentials: true });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const features = {
    free: [
      { name: 'Sesiones guardadas', value: '1', included: true },
      { name: 'Comparar pilotos', value: true, included: true },
      { name: 'Vuelta por vuelta', value: true, included: true },
      { name: 'Mejores parciales', value: false, included: false },
      { name: 'Análisis de consistencia', value: false, included: false },
      { name: 'Comparar entre sesiones', value: false, included: false },
      { name: 'Temporizador de Box', value: false, included: false },
      { name: 'Eliminar sesiones', value: false, included: false },
      { name: 'Simracing (Assetto Corsa)', value: false, included: false },
    ],
    pro: [
      { name: 'Sesiones guardadas', value: 'Ilimitadas', included: true },
      { name: 'Comparar pilotos', value: true, included: true },
      { name: 'Vuelta por vuelta', value: true, included: true },
      { name: 'Mejores parciales', value: true, included: true },
      { name: 'Análisis de consistencia', value: true, included: true },
      { name: 'Comparar entre sesiones', value: true, included: true },
      { name: 'Temporizador de Box', value: true, included: true },
      { name: 'Eliminar sesiones', value: true, included: true },
      { name: 'Simracing (Assetto Corsa)', value: false, included: false },
    ],
    simracing: [
      { name: 'Sesiones guardadas', value: 'Ilimitadas', included: true },
      { name: 'Comparar pilotos', value: true, included: true },
      { name: 'Vuelta por vuelta', value: true, included: true },
      { name: 'Mejores parciales', value: true, included: true },
      { name: 'Análisis de consistencia', value: true, included: true },
      { name: 'Comparar entre sesiones', value: true, included: true },
      { name: 'Temporizador de Box', value: true, included: true },
      { name: 'Eliminar sesiones', value: true, included: true },
      { name: 'Simracing (Assetto Corsa)', value: true, included: true },
    ]
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-white border-b-4 border-red-600">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4">
              <img 
                src="/datapro-logo.png" 
                alt="DATAPRO" 
                className="h-10 w-auto object-contain"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" className="text-gray-700 hover:text-red-600">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </Button>
              </Link>
              {user ? (
                <Link to="/app">
                  <Button className="bg-red-600 hover:bg-red-700 text-white">
                    Ir a la App
                  </Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button className="bg-red-600 hover:bg-red-700 text-white">
                    Iniciar Sesión
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">
              Planes <span className="text-yellow-500">DATAPRO</span>
            </h1>
            <p className="text-xl text-gray-400">
              Elige el plan que mejor se adapte a tus necesidades
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {/* Free Plan */}
            <Card className="bg-neutral-900 border-neutral-700">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Gratis</CardTitle>
                <CardDescription className="text-gray-400">
                  Para probar la plataforma
                </CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-white">$0</span>
                  <span className="text-gray-500">/mes</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {features.free.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <X className="w-5 h-5 text-gray-600" />
                      )}
                      <span className={feature.included ? 'text-gray-300' : 'text-gray-600'}>
                        {feature.name}
                        {typeof feature.value === 'string' && feature.included && (
                          <span className="text-gray-500 ml-1">({feature.value})</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                {user?.plan === 'free' && (
                  <div className="mt-6">
                    <Button className="w-full" variant="outline" disabled>
                      Plan Actual
                    </Button>
                  </div>
                )}
                {!user && (
                  <div className="mt-6">
                    <Link to="/login">
                      <Button className="w-full" variant="outline">
                        Comenzar Gratis
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pro Monthly Plan */}
            <Card className="bg-gradient-to-b from-red-900/20 to-neutral-900 border-red-600 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-red-600 text-white text-sm font-semibold px-4 py-1 rounded-full flex items-center gap-1">
                  <Zap className="w-4 h-4" />
                  Popular
                </span>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-white">PRO Mensual</CardTitle>
                <CardDescription className="text-gray-400">
                  Para equipos profesionales
                </CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-white">${formatPriceARS(prices.pro_monthly)}</span>
                  <span className="text-gray-500"> ARS</span>
                  <p className="text-sm text-gray-500 mt-1">/mes</p>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {features.pro.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500" />
                      <span className="text-gray-300">
                        {feature.name}
                        {typeof feature.value === 'string' && (
                          <span className="text-red-400 ml-1 font-medium">({feature.value})</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {user?.plan === 'pro' ? (
                    <Button className="w-full bg-green-600" disabled>
                      <Check className="w-4 h-4 mr-2" />
                      Plan Actual
                    </Button>
                  ) : (
                    <Button 
                      className="w-full bg-red-600 hover:bg-red-700"
                      onClick={() => handleUpgrade('pro_monthly')}
                      disabled={loading}
                    >
                      {loading && selectedPlan === 'pro_monthly' ? 'Procesando...' : 'Elegir Mensual'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Pro Annual Plan */}
            <Card className="bg-gradient-to-b from-yellow-900/20 to-neutral-900 border-yellow-500 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-sm font-semibold px-4 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-4 h-4" />
                  Mejor Valor
                </span>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-white">PRO Anual</CardTitle>
                <CardDescription className="text-gray-400">
                  Ahorra 2 meses
                </CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-white">${formatPriceARS(prices.pro_annual)}</span>
                  <span className="text-gray-500"> ARS</span>
                  <p className="text-sm text-gray-500 mt-1">/año</p>
                  <p className="text-sm text-green-400 mt-1 font-medium">¡Ahorras 2 meses!</p>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {features.pro.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500" />
                      <span className="text-gray-300">
                        {feature.name}
                        {typeof feature.value === 'string' && (
                          <span className="text-yellow-400 ml-1 font-medium">({feature.value})</span>
                        )}
                      </span>
                    </li>
                  ))}
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-yellow-500" />
                    <span className="text-yellow-400 font-medium">2 meses gratis incluidos</span>
                  </li>
                </ul>
                <div className="mt-6">
                  {user?.plan === 'pro' || user?.plan === 'simracing' ? (
                    <Button className="w-full bg-green-600" disabled>
                      <Check className="w-4 h-4 mr-2" />
                      Plan Actual
                    </Button>
                  ) : (
                    <Button 
                      className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-semibold"
                      onClick={() => handleUpgrade('pro_annual')}
                      disabled={loading}
                    >
                      {loading && selectedPlan === 'pro_annual' ? 'Procesando...' : 'Elegir Anual'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Simracing Plan */}
            <Card className="bg-gradient-to-b from-orange-900/30 to-neutral-900 border-orange-500 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-orange-500 to-red-600 text-white text-sm font-semibold px-4 py-1 rounded-full flex items-center gap-1">
                  <Gamepad2 className="w-4 h-4" />
                  Simracing
                </span>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-white">Simracing</CardTitle>
                <CardDescription className="text-gray-400">
                  Analytics + Assetto Corsa
                </CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-white">${formatPriceARS(prices.simracing_monthly)}</span>
                  <span className="text-gray-500"> ARS</span>
                  <p className="text-sm text-gray-500 mt-1">/mes</p>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {features.simracing.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <X className="w-5 h-5 text-gray-600" />
                      )}
                      <span className={feature.included ? 'text-gray-300' : 'text-gray-600'}>
                        {feature.name}
                        {typeof feature.value === 'string' && feature.included && (
                          <span className="text-orange-400 ml-1 font-medium">({feature.value})</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {user?.plan === 'simracing' ? (
                    <Button className="w-full bg-green-600" disabled>
                      <Check className="w-4 h-4 mr-2" />
                      Plan Actual
                    </Button>
                  ) : (
                    <Button 
                      className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold"
                      onClick={() => handleUpgrade('simracing_monthly')}
                      disabled={loading}
                      data-testid="pricing-simracing-btn"
                    >
                      {loading && selectedPlan === 'simracing_monthly' ? 'Procesando...' : 'Elegir Simracing'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* FAQ or Contact */}
          <div className="mt-16 text-center">
            <p className="text-gray-400 mb-4">¿Tienes preguntas?</p>
            <div className="flex justify-center gap-6">
              <a 
                href="https://wa.me/543492394950"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-green-500 hover:text-green-400"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
              <a 
                href="mailto:dataproargentina@gmail.com"
                className="flex items-center gap-2 text-red-500 hover:text-red-400"
              >
                <Mail className="w-5 h-5" />
                Email
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-950 border-t border-red-900/30 py-8 px-6">
        <div className="container mx-auto text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} DATAPRO Argentina. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
